import { PotluckData, IPotluckData } from "data";
import { IMenu, IPotluck } from "interfaces";

export interface IPotluckService {
  all(): Promise<IPotluck[]>;
  mine(id: string): Promise<IPotluck[]>;
  findById(id: string): Promise<IPotluck>;
  nfindById(id: string): Promise<IPotluck>;
  insert(payload: IPotluck): Promise<IPotluck>;
  insertAll(payload: IPotluck[]): Promise<IPotluck[]>;
  update(payload: IPotluck): Promise<IPotluck>;
  delete(id: string): Promise<boolean>;
  addParticipants(id: string, user: string[]): Promise<IPotluck>;
  removeParticipants(id: string, user: string[]): Promise<IPotluck>;
  voting(userId: string, potluckId: string, menu: IMenu[]): Promise<IPotluck>;
}

export class PotluckService implements IPotluckService {
  potluckData: IPotluckData;

  constructor() {
    this.potluckData = new PotluckData();
  }

  /**
   * fetch all the potlucks
   * @returns {IPotluck[]} a list of potlucks
   */

  async all() {
    const { payload: response } = await this.potluckData.all();
    return response;
  }

  /**
   * fetch all my potlucks
   * @param {string} id the user id
   * @returns {IPotluck[]} a list of potlucks
   */

  async mine(id: string) {
    if (id === "") return [];
    const { payload: response } = await this.potluckData.findAll({ $or: [{ "created_by": id }, { "participants": id }] } as any);
    return response.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  /**
   * fetch a potluck by id
   * @param {string} id the potluck id
   * @returns {IPotluck} a potluck if found
   */

  async findById(id: string) {
    if (id === "") return null;
    const { payload: response } = await this.potluckData.find({ _id: id }, true);
    return response;
  }

  /**
   * fetch a potluck by id (not populated)
   * @param {string} id the potluck id
   * @returns {IPotluck} a potluck if found
   */

  async nfindById(id: string) {
    if (id === "") return null;
    const { payload: response } = await this.potluckData.find({ _id: id }, false);
    return response;
  }

  /**
   * Insert a potluck to the database
   * @param {IPotluck} payload the potluck data
   * @returns {IPotluck} the inserted potluck
   */

  async insert(payload: IPotluck) {
    const { payload: response } = await this.potluckData.insert([payload]);
    return response[0];
  }

  /**
   * Insert multiple potlucks to the database
   * @param {IPotluck[]} payload the array of potluck data
   * @returns {IPotluck[]} the inserted potlucks
   */

  async insertAll(payload: IPotluck[]) {
    const { payload: response } = await this.potluckData.insert(payload);
    return response;
  }

  /**
   * update a potluck
   * @param {IPotluck} payload the potluck to update
   * @returns {IPotluck} the updated potluck
   */

  async update(payload: IPotluck) {
    const { payload: response } = await this.potluckData.update(payload);
    return response;
  }

  /**
   * Delete a potluck by id
   * @param {string} id the potluck id
   * @returns {boolean} true if deleted, false otherwise
   */

  async delete(id: string) {
    const { payload: response } = await this.potluckData.delete(id);
    return response;
  }

  /**
   * Add participants to a potluck
   * @param {string} id the id of the potluck
   * @param {string[]} user the array of user ids
   * @returns {IPotluck} the updated potluck
   */

  async addParticipants(id: string, user: string[]): Promise<IPotluck> {
    const { payload: response } = await this.potluckData.addParticipants(id, user);
    return response;
  }

  /**
   * Remove participants from a potluck
   * @param {string} id - The id of the potluck
   * @param {string[]} user - The array of user ids to remove
   * @returns {IPotluck} - The updated potluck
   */

  async removeParticipants(id: string, user: string[]): Promise<IPotluck> {
    const { payload: response } = await this.potluckData.removeParticipants(id, user);
    return response;
  }

  /**
   * Vote on a potluck
   * @param {string} userId - The id of the user voting
   * @param {string} potluckId - The id of the potluck
   * @param {IMenu[]} menu - The menu items to vote on
   * @returns {IPotluck} - The updated potluck
   */

  async voting(userId: string, potluckId: string, menu: IMenu[]) {
    const { payload: response } = await this.potluckData.voting(userId, potluckId, menu);
    return response;
  }
}